import EventEmitter from '@ambire-common/controllers/eventEmitter/eventEmitter'
import { IEventEmitterRegistryController } from '@ambire-common/interfaces/eventEmitter'
import { storage } from '@common/services/storage'
import { railgunEmitter } from '@web/utils/railgunListener'

// Mirror of the extension's AUTO_LOCK_TIMES so the shared Settings UI
// (useControllerState('AutoLockController') / AUTO_LOCK_CONTROLLER_SET_AUTO_LOCK_TIME)
// reads identical values. Duplicated rather than imported from
// `@web/.../controllers/auto-lock` because that module pulls `browser.alarms`,
// which does not exist on a plain web page.
// eslint-disable-next-line @typescript-eslint/naming-convention
export enum AUTO_LOCK_TIMES {
  never = 0,
  _7days = 10080,
  _1day = 1440,
  _8hours = 480,
  _1hour = 60,
  _10minutes = 10
}

// Web is a higher-risk surface than the extension (no process isolation), so we
// default to locking after inactivity instead of `never`.
const WEB_DEFAULT_AUTO_LOCK = AUTO_LOCK_TIMES._10minutes

// When the tab is hidden we lock sooner: an unlocked wallet sitting in a
// backgrounded tab is exactly the "walked away" case. Capped so a long
// `autoLockTime` (e.g. 8h) still locks promptly once the tab is hidden.
const HIDDEN_MAX_MS = 5 * 60 * 1000

// High-frequency sources (scroll/wheel, RAILGUN scan progress) call
// setLastActiveTime() constantly; rescheduling a minutes-scale timer more than
// once a second is pointless.
const RESET_THROTTLE_MS = 1000

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const

/**
 * Web counterpart of the extension's `AutoLockController`. The extension version
 * uses `browser.alarms`, which web pages don't have, so this uses `setTimeout`
 * plus DOM activity / visibility listeners. The class name must stay
 * `AutoLockController` — controllers are keyed in the event-emitter registry by
 * `constructor.name`, and the shared Settings UI subscribes to that name.
 *
 * Self-contained on purpose: it attaches its own activity listeners rather than
 * relying on `AutoLockControllerStateProvider` (react-idle-timer), which is not
 * mounted in the mobile-web router tree.
 *
 * Phase 2.1: it also treats RAILGUN merkletree scan progress as activity. The
 * first private-balance sync takes 15–20 min with no mouse/keyboard input; a
 * naive idle timer would lock mid-sync. Subscribing to the scan emitter keeps
 * the timer deferred while a real sync is in flight.
 */
export class AutoLockController extends EventEmitter {
  isReady: boolean = false

  #_autoLockTime: AUTO_LOCK_TIMES = WEB_DEFAULT_AUTO_LOCK

  #onAutoLock: () => void

  #timer: ReturnType<typeof setTimeout> | undefined = undefined

  #lastResetAt: number = 0

  #listenersAttached: boolean = false

  get autoLockTime() {
    return this.#_autoLockTime
  }

  set autoLockTime(newValue: AUTO_LOCK_TIMES) {
    // The Settings dropdown dispatches the value as a STRING. Coerce to a number
    // so (a) getAutoLockLabel's strict `=== AUTO_LOCK_TIMES._x` matches and the
    // label renders, and (b) the `never` case is `0` (falsy) — a string "0"
    // would be truthy and schedule a 0ms instant-lock loop.
    this.#_autoLockTime = AutoLockController.#normalizeTime(newValue)
    void storage.set('autoLockTime', this.#_autoLockTime)
    // Apply immediately, bypassing the throttle — an explicit settings change
    // should take effect now, not up to RESET_THROTTLE_MS later.
    this.#scheduleTimer()
    this.emitUpdate()
  }

  static #normalizeTime(value: unknown): AUTO_LOCK_TIMES {
    const n = Number(value)
    return Number.isFinite(n) ? (n as AUTO_LOCK_TIMES) : AUTO_LOCK_TIMES.never
  }

  constructor(eventEmitterRegistry: IEventEmitterRegistryController, onAutoLock: () => void) {
    super(eventEmitterRegistry)
    this.#onAutoLock = onAutoLock
    void this.#init()
  }

  async #init(): Promise<void> {
    // Coerce: a value persisted by an older build (or the string-valued Settings
    // dropdown) may be a string; normalize so timer math and label both work.
    this.#_autoLockTime = AutoLockController.#normalizeTime(
      await storage.get('autoLockTime', WEB_DEFAULT_AUTO_LOCK)
    )
    this.#attachListeners()
    this.#scheduleTimer()
    this.isReady = true
    this.emitUpdate()
  }

  #attachListeners() {
    if (this.#listenersAttached || typeof window === 'undefined') return
    this.#listenersAttached = true

    for (const evt of ACTIVITY_EVENTS) {
      window.addEventListener(evt, this.#onActivity, { passive: true })
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.#onVisibilityChange)
    }
    // A backgrounded sync is still activity; without this the 15–20 min first
    // private sync (no user input) would trip the idle timer mid-scan.
    railgunEmitter.on('utxoScanUpdate', this.#onActivity)
    railgunEmitter.on('txidScanUpdate', this.#onActivity)
  }

  #onActivity = () => {
    this.setLastActiveTime()
  }

  #onVisibilityChange = () => {
    // Re-evaluate the timer: visible → full window (and counts as activity),
    // hidden → shorter HIDDEN_MAX_MS cap.
    this.#scheduleTimer()
  }

  #clearTimer() {
    if (this.#timer) {
      clearTimeout(this.#timer)
      this.#timer = undefined
    }
  }

  #scheduleTimer() {
    this.#clearTimer()
    if (!this.#_autoLockTime) return // `never`

    const fullMs = this.#_autoLockTime * 60 * 1000
    const isHidden = typeof document !== 'undefined' && document.hidden
    const ms = isHidden ? Math.min(fullMs, HIDDEN_MAX_MS) : fullMs

    this.#timer = setTimeout(() => {
      this.#clearTimer()
      this.#onAutoLock()
    }, ms)
  }

  setLastActiveTime() {
    const now = Date.now()
    if (now - this.#lastResetAt < RESET_THROTTLE_MS) return
    this.#lastResetAt = now
    this.#scheduleTimer()
  }

  destroy() {
    this.#clearTimer()
    if (!this.#listenersAttached) return
    this.#listenersAttached = false
    if (typeof window !== 'undefined') {
      for (const evt of ACTIVITY_EVENTS) {
        window.removeEventListener(evt, this.#onActivity)
      }
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.#onVisibilityChange)
    }
    railgunEmitter.off('utxoScanUpdate', this.#onActivity)
    railgunEmitter.off('txidScanUpdate', this.#onActivity)
  }

  toJSON() {
    return {
      ...this,
      ...super.toJSON(),
      autoLockTime: this.autoLockTime
    }
  }
}

export default AutoLockController
